/**
 * Every data call the two screens make, in one place.
 *
 * All calls go to the Kit19 REST API (Kit19API_V2) through `apiPost`. There are
 * no .asmx / WCF calls. With `USE_MOCK` on, each function returns sample data of
 * the same shape so the screens run without a backend.
 */
import { cfg } from 'config/runtimeConfig'
import type {
  ActivityItem,
  AppointmentItem,
  CallLogDetail,
  CallLogItem,
  ChatLogItem,
  CommentItem,
  CustomFieldDef,
  CustomFieldValue,
  DocumentItem,
  Enquiry,
  EnquirySavePayload,
  FieldMask,
  FollowUpItem,
  HashedUser,
  InteractionHistory,
  Lead,
  LeadSavePayload,
  LeadScore,
  NoteItem,
  Option,
  PermissionMap,
  PhysicalAppointmentDetail,
  PipelineStage,
  TaskDetail,
  TaskItem,
  WebformItem,
  FollowupPayload,
  SmsPayload,
  EmailPayload,
  VoicePayload,
  WhatsappPayload,
  NotePayload,
  DocumentPayload,
  ActionTaskPayload,
  ActionAppointmentPayload,
  ActionDealPayload,
  WaChannelTemplate,
} from 'types/entity'
import { API_BASE_URL, API_TOKEN, USE_MOCK, apiPost, apiPostRaw, getSessionOverrides } from './client'
import { getSsoSession } from 'auth/ssoClient'
import { ROUTES } from './endpoints'
import {
  mapCustomFieldValues,
  mapEnquiry,
  mapLead,
  mapPipelineStages,
  type LeadDetailResponse,
} from './mappers'
import * as mock from './mockData'

const delay = (ms = 220) => new Promise<void>((resolve) => setTimeout(resolve, ms))

async function mocked<T>(value: T, ms?: number): Promise<T> {
  await delay(ms)
  return value
}

/** Session values the .aspx master page used to expose as hidden fields. */
export interface SessionContext {
  parentId: number
  userId: number
  /** hdnModuleCode — identifies the page to the permission and masking APIs. */
  moduleCode: string
  /** hdnSipTimeZone — passed to the interaction-history endpoint. */
  timeZone: string
}

/**
 * Session identity, in priority order:
 *   1. the (encrypted) link's own UserId/ParentId (query-string overrides) —
 *      mirrors the hidden fields the legacy master page rendered;
 *   2. the SSO session stored by /callback (shared kit_* localStorage keys,
 *      same as the Kit19Sales module);
 *   3. `.env` values — local development fallback only.
 */
export function getSessionContext(): SessionContext {
  const overrides = getSessionOverrides()
  const sso = getSsoSession()
  const pId = overrides.parentId ?? sso.parentId ?? Number(cfg('REACT_APP_PARENT_ID') || 0)
  const uId = overrides.userId ?? sso.userId ?? Number(cfg('REACT_APP_USER_ID') || 0)
  return {
    parentId: pId > 0 ? pId : 0,
    userId: uId > 0 ? uId : 0,
    moduleCode: overrides.moduleCode ?? cfg('REACT_APP_MODULE_CODE') ?? '',
    timeZone: overrides.timeZone || sso.timezone || (cfg('REACT_APP_TIMEZONE') ?? ''),
  }
}

const toNumber = (value: string): number => Number(value) || 0

/**
 * Coerces any API value into something safe to render.
 *
 * Several endpoints answer with a nested object where a name is expected —
 * IMSMOB/GetConversationAsRequiredConvId returns `Sender`/`Recipient` as a
 * ConversationUser ({ Id, PersonName, ProfileImgFileName, Source, MobileNo }),
 * and the task/followup rows can carry an agent object. Passing one of those
 * straight into JSX throws "Objects are not valid as a React child" and takes
 * the whole screen down, so every mapped display string goes through here.
 */
export function asText(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ')
  if (typeof value === 'object') {
    const row = value as Record<string, unknown>
    for (const key of ['PersonName', 'Name', 'DisplayName', 'UserName', 'Text', 'Title', 'FullName', 'AgentName']) {
      const found = row[key]
      if (typeof found === 'string' && found.trim() !== '') return found
    }
    return ''
  }
  return ''
}

/**
 * Resolves a site-relative asset path returned by the API (activity event
 * icons, avatars) against the Kit19 host, so those images load from the legacy
 * site exactly as the .aspx pages served them. Absolute URLs and data URIs are
 * returned untouched.
 */
export function legacyAssetUrl(path: string): string {
  if (!path) return ''
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('data:')) return path
  const base = (cfg('REACT_APP_LEGACY_BASE_URL') ?? '').replace(/\/+$/, '')
  if (!base) return path
  return `${base}/${path.replace(/^\/+/, '')}`
}

/**
 * Guards every list response.
 *
 * The API returns `Details: null` for "no rows", and some routes answer with a
 * scalar or an object. Without this guard those cases reach `.map` and take the
 * whole tab down with "x.map is not a function".
 */
function asArray<T>(value: T[] | null | undefined): T[] {
  if (Array.isArray(value)) return value
  if (value == null || (value as unknown) === '') return []
  // A JSON array delivered as a string (a few routes do this).
  if (typeof (value as unknown) === 'string') {
    try {
      const parsed = JSON.parse(value as unknown as string)
      return Array.isArray(parsed) ? (parsed as T[]) : []
    } catch {
      return []
    }
  }
  return []
}

/* ------------------------------------------------------------------ view */

/**
 * `GetLeadDetailByLeadIdNew` returns the record, its custom fields, its deal
 * stages and its mask data together. The raw response is cached briefly so the
 * view screen's four panels share one network call.
 */
let leadDetailCache: { key: string; promise: Promise<LeadDetailResponse | null> } | null = null

/**
 * NOTE: the caller's AbortSignal is deliberately NOT forwarded.
 *
 * This promise is shared by the profile panel, the custom-field drawer and the
 * pipeline. If one consumer's unmount aborted it, every other consumer would
 * see an aborted request — which is exactly what React StrictMode's
 * mount → unmount → remount cycle causes in dev: the first mount aborts the
 * request, the second remount then reuses the already-rejected cached promise
 * and the screen shows "Record not found".
 */
function loadLeadDetail(leadId: string, _signal?: AbortSignal): Promise<LeadDetailResponse | null> {
  const { userId } = getSessionContext()
  const key = `${leadId}|${userId}`

  if (leadDetailCache?.key === key) return leadDetailCache.promise

  const promise = USE_MOCK
    ? mocked(mock.mockLeadDetailResponse)
    : apiPost<LeadDetailResponse | null>(ROUTES.leadDetail, { LeadId: toNumber(leadId), UserId: userId })

  leadDetailCache = { key, promise }
  // A failed load must not be cached, or a retry would replay the error.
  promise.catch(() => {
    if (leadDetailCache?.key === key) leadDetailCache = null
  })

  return promise
}

/** Call after a save so the next read re-fetches. */
export function invalidateLeadDetail(): void {
  leadDetailCache = null
}

/**
 * True when a mapped record carries nothing that identifies it.
 *
 * GetLeadDetailByLeadIdNew / GetEnquiryDetailByIdNew answer `Status: 1` with an
 * empty `PredefinedFields` for a record that does not exist *or* belongs to
 * another account — the mappers then hand back an object full of empty strings,
 * which the screen rendered as a page of dashes with no explanation. Treating
 * that as "no record" lets the caller say so instead.
 */
function isBlankRecord(record: { leadNo?: string; personName?: string; mobileNo?: string; emailId?: string }): boolean {
  return !record.leadNo && !record.personName && !record.mobileNo && !record.emailId
}

export async function fetchLead(leadId: string, signal?: AbortSignal): Promise<Lead | null> {
  const record = mapLead(await loadLeadDetail(leadId, signal), leadId)
  return isBlankRecord(record) ? null : record
}

/* -------------------------------------------------- record from a call */

/** The record a phone call belongs to. */
export interface CallEntityMatch {
  entityType: 'lead' | 'enquiry'
  entityId: string
  personName: string
}

interface MobileMatchRow {
  EntityId?: number | string
  EntityName?: string
  PersonName?: string
  [column: string]: unknown
}

const digitsOnly = (value: unknown): string => String(value ?? '').replace(/\D/g, '')

/**
 * The lead or enquiry with this mobile number in the signed-in user's account.
 * `entityType` "lead" / "enquiry" searches only that type; otherwise leads come first, then
 * enquiries. Within a type, a record whose number carries the same country code is preferred.
 * A number sent with its country code in front (919876543210) is matched without it, and a
 * record stored with a leading 0 is found too.
 */
export async function findEntityByMobile(
  query: { countryCode: string; mobileNo: string; entityType?: string },
  signal?: AbortSignal,
): Promise<CallEntityMatch | null> {
  const countryCode = digitsOnly(query.countryCode)
  let mobile = digitsOnly(query.mobileNo)
  if (countryCode && mobile.length > 10 && mobile.startsWith(countryCode)) mobile = mobile.slice(countryCode.length)
  mobile = mobile.replace(/^0+/, '')
  if (!mobile) return null

  const wanted = String(query.entityType ?? '').trim().toLowerCase()
  const typeOrder: Array<'lead' | 'enquiry'> =
    wanted === 'lead' || wanted === 'enquiry' ? [wanted] : ['lead', 'enquiry']

  if (USE_MOCK) return mocked(null)

  const { userId } = getSessionContext()
  const matches: Array<{ number: string; row: MobileMatchRow }> = []
  const seen = new Set<string>()
  for (const number of [mobile, `0${mobile}`]) {
    const rows = await apiPost<MobileMatchRow[] | null>(
      ROUTES.matchByMobile,
      { UserId: userId, Mode: 'mobile', Value: number, Offsets: 0, PageSize: 50 },
      { signal },
    )
    for (const row of Array.isArray(rows) ? rows : []) {
      const key = `${row.EntityName}|${row.EntityId}`
      if (seen.has(key)) continue
      seen.add(key)
      matches.push({ number, row })
    }
  }

  for (const type of typeOrder) {
    const ofType = matches.filter((m) => String(m.row.EntityName ?? '').trim().toLowerCase() === type)
    if (!ofType.length) continue
    const sameCountry = countryCode
      ? ofType.find((m) =>
          ['', '1', '2'].some(
            (slot) =>
              String(m.row[`MobileNo${slot}`] ?? '').trim() === m.number &&
              digitsOnly(m.row[`CountryCode${slot}`]) === countryCode,
          ),
        )
      : undefined
    const hit = (sameCountry ?? ofType[0]).row
    return { entityType: type, entityId: String(hit.EntityId ?? ''), personName: String(hit.PersonName ?? '') }
  }
  return null
}

/** A row of the launcher's recent-records list. */
export interface EntitySummary {
  entityId: string
  entityNo: string
  personName: string
  companyName: string
  mobileNo: string
}

/**
 * Recent leads for the launcher, so it links to real records instead of
 * hard-coded ids. `GetLeadList` returns its rows as a JSON string.
 */
export async function fetchLeadList(limit = 25, signal?: AbortSignal): Promise<EntitySummary[]> {
  if (USE_MOCK) {
    return mocked([
      { entityId: '100916', entityNo: '43', personName: 'Bhoomi Mittal', companyName: 'Kit19 Technologies', mobileNo: '+91 9990998798' },
      { entityId: '19237971', entityNo: '20268112', personName: 'Live Sample Lead', companyName: 'Kit19 Sample Entity', mobileNo: '+91 9876543210' },
    ])
  }

  const { parentId, userId } = getSessionContext()
  const res = await apiPost<RawLeadListRow[]>(
    ROUTES.leadList,
    {
      UserId: userId,
      ParentId: parentId,
      FilterExp: '',
      OrderStr: '',
      StartDate: '',
      EndDate: '',
      Columns: '',
      Mode: '',
      OutputAllCols: true,
      OffsetValue: 0,
      PagingSize: limit,
    },
    { signal },
  )

  return asArray(res)
    .slice(0, limit)
    .map((row) => ({
      entityId: String(row.LeadId ?? ''),
      entityNo: String(row.LeadNo ?? ''),
      personName: row.PersonName ?? '',
      companyName: row.CompanyName ?? '',
      mobileNo: [row.CountryCode1, row.MobileNo1].filter(Boolean).join(' '),
    }))
    .filter((row) => row.entityId !== '')
}

interface RawLeadListRow {
  LeadId?: number | string
  LeadNo?: number | string
  PersonName?: string
  CompanyName?: string
  CountryCode1?: string
  MobileNo1?: string
}

/**
 * `GetEnquiryDetailByIdNew` bundles PredefinedFields, CustomFields and MaskData
 * the same way the lead endpoint does, so it is cached and shared too.
 */
let enquiryDetailCache: { key: string; promise: Promise<LeadDetailResponse | null> } | null = null

/** Shared like `loadLeadDetail`, so the caller's signal is not forwarded either. */
function loadEnquiryDetail(enquiryId: string, _signal?: AbortSignal): Promise<LeadDetailResponse | null> {
  const { userId } = getSessionContext()
  const key = `${enquiryId}|${userId}`

  if (enquiryDetailCache?.key === key) return enquiryDetailCache.promise

  const promise = USE_MOCK
    ? mocked({ PredefinedFields: mock.mockEnquiryDetail, CustomFields: [] } as LeadDetailResponse)
    : apiPost<LeadDetailResponse | null>(
        ROUTES.enquiryDetail,
        { EnquiryId: toNumber(enquiryId), UserId: userId },
        {},
      )

  enquiryDetailCache = { key, promise }
  promise.catch(() => {
    if (enquiryDetailCache?.key === key) enquiryDetailCache = null
  })

  return promise
}

export function invalidateEnquiryDetail(): void {
  enquiryDetailCache = null
}

export async function fetchEnquiry(enquiryId: string, signal?: AbortSignal): Promise<Enquiry | null> {
  const record = mapEnquiry(await loadEnquiryDetail(enquiryId, signal), enquiryId)
  // enquiryId is echoed back from the request, so it proves nothing here —
  // only the fields that come from the payload do.
  return isBlankRecord({ personName: record.personName, mobileNo: record.mobileNo, emailId: record.emailId })
    ? null
    : record
}

export async function fetchViewCustomFields(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  signal?: AbortSignal,
): Promise<CustomFieldValue[]> {
  if (entityType === 'lead') {
    // Already included in the lead response — no extra request.
    return mapCustomFieldValues(await loadLeadDetail(entityId, signal))
  }

  if (USE_MOCK) return mocked(mock.mockViewCustomFields)

  // For an enquiry, the entity-aware endpoint is the correct one — the
  // GetCustomeFieldNameAndValueByParentAndLeadId route is lead-only.
  const defs = await fetchEditCustomFields('enquiry', entityId, signal)
  return defs.map((def) => ({ label: def.fieldname, value: def.fieldvalue ?? '' }))
}

export async function fetchPipelineProgress(
  leadId: string,
  signal?: AbortSignal,
): Promise<PipelineStage[]> {
  const detail = await loadLeadDetail(leadId, signal)
  return mapPipelineStages(detail?.dealStageList)
}

export async function fetchLeadScore(leadId: string, signal?: AbortSignal): Promise<LeadScore | null> {
  if (USE_MOCK) return mocked(mock.mockLeadScore)

  const res = await apiPost<ScoreCardRow[] | null>(ROUTES.scoreCard, { LeadID: toNumber(leadId) }, { signal })
  const row = Array.isArray(res) ? res[0] : res
  if (!row) return null

  // Top3ScoringFactors / Top3Scores are parallel comma-separated lists.
  const factors = (row.Top3ScoringFactors ?? '').split(',').map((s) => s.trim()).filter(Boolean)
  const scores = (row.Top3Scores ?? '').split(',').map((s) => s.trim())

  return {
    score: Number(row.CurrentScore ?? 0) || 0,
    personName: row.PersonName ?? '',
    companyName: row.CompanyName ?? '',
    details: factors.length
      ? factors.map((factor, i) => ({ label: factor, value: scores[i] ?? '' }))
      : [{ label: 'Score', value: String(row.ScorePRCNTG ?? row.CurrentScore ?? '') }],
  }
}

/** Mirrors LeadScoreCardDetails in BO/UserCRM/Lead_BO.cs. */
interface ScoreCardRow {
  PersonName?: string
  CompanyName?: string
  CurrentScore?: string
  ScorePRCNTG?: string
  Top3Scores?: string
  Top3ScoringFactors?: string
}

/** `UserCRM/GetLeadActivity` → `LeadActivity_List[]`. */
export async function fetchActivities(entityId: string, signal?: AbortSignal): Promise<ActivityItem[]> {
  if (USE_MOCK) return mocked(mock.mockActivities)

  const res = await apiPost<LeadActivityRow[]>(
    ROUTES.activities,
    { LeadId: toNumber(entityId), Start: 0, Limit: 50 },
    { signal },
  )

  return asArray(res).map((row, i) => ({
    id: String(row.HistoryID ?? i),
    // EventType is the short kind ("Chat", "Followup", ...); EventName is the headline.
    type: asText(row.EventType) || asText(row.EventName) || 'Activity',
    title: asText(row.EventName) || 'Activity',
    description: asText(row.EventDescription),
    createdOn: [asText(row.EventDate), asText(row.EventTime)].filter(Boolean).join(' '),
    createdBy: asText(row.UserName),
    // The same per-event image the legacy feed rendered from EventIconURL; the
    // API returns a site-relative path, so resolve it against the Kit19 host.
    iconUrl: legacyAssetUrl(asText(row.EventIconURL)),
  }))
}

/** Mirrors LeadActivity_List in BO/UserCRM/Lead_BO.cs. */
interface LeadActivityRow {
  HistoryID?: string
  UserName?: string
  EventDescription?: string
  EventDate?: string
  EventTime?: string
  EventName?: string
  EventIconURL?: string
  EventType?: string
}

export async function fetchNotes(entityId: string, signal?: AbortSignal): Promise<NoteItem[]> {
  if (USE_MOCK) return mocked(mock.mockNotes)

  const { parentId, userId } = getSessionContext()
  const res = await apiPost<RawNote[]>(
    ROUTES.notes,
    { leadid: entityId, parentid: parentId, userid: userId },
    { signal },
  )

  return asArray(res).map((row, i) => ({
    id: String(row.NoteID ?? i),
    note: row.Notes ?? '',
    createdOn: asText(row.CreatedDate),
    createdBy: asText(row.CreatedBy),
  }))
}

/** Mirrors NotesEntity in BO/UserCRM/Lead_BO.cs. */
interface RawNote {
  NoteID?: number
  Notes?: string
  CreatedDate?: string
  CreatedBy?: string
}

export async function fetchDocuments(entityId: string, signal?: AbortSignal): Promise<DocumentItem[]> {
  if (USE_MOCK) return mocked(mock.mockDocuments)

  const { parentId } = getSessionContext()
  const res = await apiPost<RawDocument[]>(
    ROUTES.documents,
    { LeadID: toNumber(entityId), ParentID: parentId },
    { signal },
  )

  return asArray(res).map((row, i) => ({
    id: String(row.DocUpID ?? i),
    fileName: row.DocumentName ?? '',
    filePath: row.FileLocation ?? row.FileLocation2 ?? '',
    uploadedOn: row.UploadedDate ?? '',
    uploadedBy: asText(row.UserName),
  }))
}

/** Mirrors Upload_BO in BO/UserCRM/Upload_BO.cs. */
interface RawDocument {
  DocUpID?: number
  DocumentName?: string
  FileLocation?: string
  FileLocation2?: string
  UploadedDate?: string
  UserName?: string
}

/**
 * Deletes a document and records it on the lead's activity feed, matching the
 * legacy `OnClickDocumentDelete`, which called DeleteDocument then
 * DeleteDocLeadActivity. The activity log is best-effort: failing to record it
 * must not make a successful delete look like a failure.
 */
export async function deleteDocument(documentId: string, entityId?: string, documentName?: string): Promise<void> {
  if (USE_MOCK) {
    await delay(150)
    return
  }

  const { userId, parentId } = getSessionContext()
  await apiPost(ROUTES.deleteDocument, { DocUpID: toNumber(documentId), UserId: userId })

  try {
    // Legacy sent the doc *name* — Usp_EventTemplate_DynamicVarReplacement puts
    // it into the "Doc deleted" activity text.
    await apiPost(ROUTES.deleteDocActivity, {
      strLeadID: entityId ?? '',
      strDocName: documentName ?? '',
      UserID: userId,
      ParentID: parentId,
    })
  } catch (err) {
    console.warn('Document deleted, but the activity entry could not be written.', err)
  }
}

/** Port of `disp_hyperlink` — resolves a stored document to a viewable URL. */
export async function fetchDocumentLink(documentId: string, signal?: AbortSignal): Promise<string> {
  if (USE_MOCK) return mocked('')

  const { parentId } = getSessionContext()
  const res = await apiPost<string | { FileUrl?: string } | null>(
    ROUTES.documentLink,
    { DocUpID: toNumber(documentId), ParentId: parentId },
    { signal },
  )
  if (!res) return ''
  return typeof res === 'string' ? res : (res.FileUrl ?? '')
}

/** Port of `Filter.OutcomeList` — outcomes used by the activity filter. */
export async function fetchOutcomes(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])

  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.outcomes, { UserId: userId }, { signal })
  return toOptions(res, 'OutcomeId', 'OutcomeName')
}

/** Port of `funcToDisplayTagListWithFilter` — suggestions for the tag box. */
export async function fetchTagSuggestions(filter: string, signal?: AbortSignal): Promise<string[]> {
  if (USE_MOCK) return mocked(['Hot', 'Warm', 'Enterprise'], 120)

  const { userId } = getSessionContext()
  const res = await apiPost<Array<{ Tag?: string }>>(
    ROUTES.tagList,
    { UserId: userId, Filter: filter },
    { signal },
  )
  return asArray(res)
    .map((row) => row.Tag ?? '')
    .filter(Boolean)
}

export async function fetchTasks(entityId: string, signal?: AbortSignal): Promise<TaskItem[]> {
  if (USE_MOCK) return mocked(mock.mockTasks)

  const rows = await requestTaskList(entityId, signal)
  return rows.filter((row) => !isAppointmentRow(row)).map((row, i) => ({
    id: String(row.Id ?? i),
    subject: asText(row.Title),
    status: asText(row.Status),
    priority: row.Intent ?? '',
    dueDate: row.EndDate ?? '',
    assignedTo: asText(row.AgentName),
  }))
}

export async function fetchAppointments(
  entityId: string,
  signal?: AbortSignal,
): Promise<AppointmentItem[]> {
  if (USE_MOCK) return mocked(mock.mockAppointments)

  const rows = await requestTaskList(entityId, signal)
  return rows.filter(isAppointmentRow).map((row, i) => ({
    id: String(row.Id ?? i),
    subject: asText(row.Title),
    status: asText(row.Status),
    startDate: row.StartDate ?? '',
    endDate: row.EndDate ?? '',
    location: row.EntityName ?? '',
    assignedTo: asText(row.AgentName),
  }))
}

/**
 * One request serves both the Task and Appointment tabs.
 *
 * `Mode` is passed through to the stored procedure; "A" is the value the
 * controller declares as its default. The two tabs are separated client-side on
 * `rectype` so neither depends on a guessed Mode code.
 */
const taskListCache = new Map<string, Promise<RawTask[]>>()

/** Shared by the Task and Appointment tabs, so the caller's signal is not forwarded. */
async function requestTaskList(entityId: string, _signal?: AbortSignal): Promise<RawTask[]> {
  const { userId } = getSessionContext()
  const key = `${entityId}|${userId}`

  const cached = taskListCache.get(key)
  if (cached) return cached

  const promise = apiPost<RawTask[]>(
    ROUTES.tasksByLead,
    { UserId: userId, LeadId: toNumber(entityId), Mode: 'A' },
    {},
  ).then((rows) => asArray<RawTask>(rows))

  taskListCache.set(key, promise)
  promise.catch(() => taskListCache.delete(key))
  return promise
}

function isAppointmentRow(row: RawTask): boolean {
  return /appoint/i.test(row.rectype ?? '')
}

/** Mirrors CalenderViewEntity in BO/UserCRM/Dashboard_BO.cs. */
interface RawTask {
  Id?: string
  Title?: string
  StartDate?: string
  EndDate?: string
  Status?: string
  Intent?: string
  AgentName?: string
  EntityName?: string
  rectype?: string
}

export async function fetchFollowUps(entityId: string, signal?: AbortSignal): Promise<FollowUpItem[]> {
  if (USE_MOCK) return mocked(mock.mockFollowUps)

  const rows = await fetchInteractionHistories(entityId, signal)
  return rows.map((row, i) => ({
    id: `${row.LeadNo || entityId}-${i}`,
    subject: asText(row.FollowupStatus),
    status: asText(row.FollowupStatus),
    followUpDate: row.NextStatusDate ?? '',
    remarks: row.Remarks ?? '',
    assignedTo: asText(row.AssignedTo),
  }))
}

export async function fetchCallLogs(
  entityId: string,
  entityType: 'lead' | 'enquiry' = 'lead',
  signal?: AbortSignal,
): Promise<CallLogItem[]> {
  if (USE_MOCK) return mocked(mock.mockCallLogs)

  const { parentId, userId } = getSessionContext()
  const wantedId = String(toNumber(entityId))
  const wantedEntity = entityType === 'enquiry' ? 'enquiry' : 'lead'

  // Preferred: the API filters by entity and returns only this record's calls.
  // Until that endpoint is deployed, fall back to scanning a recent page — the
  // stored procedure rejects an entity predicate in its FilterText, so the
  // fallback must filter client-side.
  let res: RawCallLog[] | null = null
  try {
    res = await apiPost<RawCallLog[]>(
      ROUTES.callLogListByEntity,
      {
        UserID: userId,
        parentid: parentId,
        EntityName: wantedEntity,
        EntityId: wantedId,
        OffsetValue: 0,
        // The rows are already filtered to this one record server-side, so a
        // page of 200 covers any real call history and halves the wait — the
        // 2000-row page made the Calls tab take ~14s against the live API.
        PagingSize: 200,
      },
      { signal },
    )
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    console.warn(
      'UserCRM/GetCallLogListByEntity unavailable — deploy Kit19API_V2 for complete call history. ' +
        'Falling back to a scan of recent calls.',
      err,
    )
    res = await apiPost<RawCallLog[]>(
      ROUTES.callLogList,
      {
        UserID: userId,
        parentid: parentId,
        FullText: '',
        FilterText: '',
        SearchText: '',
        OffsetValue: 0,
        PagingSize: 200,
      },
      { signal },
    )
  }

  // Live rows carry the owning record in customerentity/customerentityid; the
  // LeadId column is 0 for click-to-call rows, so filtering on it drops everything.
  return asArray(res)
    .filter((row) => {
      const byCustomerEntity =
        String(row.customerentityid ?? '') === wantedId &&
        (row.customerentity ?? '').toLowerCase() === wantedEntity
      const byLeadId = wantedEntity === 'lead' && String(row.LeadId ?? '') === wantedId && wantedId !== '0'
      return byCustomerEntity || byLeadId
    })
    .map((row, i) => ({
      id: String(row.id ?? i),
      callType: row.Direction ?? row.direction ?? row.callstatus ?? '',
      fromNumber: row.AgentMode === 'Mobile' ? (row.user_login ?? '') : String(row.callerid ?? ''),
      toNumber: row.Customernumber ?? row.MobileNo ?? row.dnid ?? '',
      callTime: row.Date ?? row.call_start_time ?? row.callcreatedon ?? '',
      duration: formatSeconds(row.Callduration ?? row.callduration ?? row.CustomerDuration),
      recordingUrl: row.RecodingUrl ?? row.recordurl ?? '',
      agent: asText(row.AgentUser) || asText(row.agentname) || asText(row.user_login),
      status: asText(row.callstatus),
    }))
}

/** "20" seconds → "20s"; "95" → "1m 35s". Live durations arrive as second counts. */
function formatSeconds(value: string | number | undefined): string {
  if (value == null || value === '') return ''
  const total = Number(value)
  if (Number.isNaN(total)) return String(value)
  if (total < 60) return `${total}s`
  return `${Math.floor(total / 60)}m ${total % 60}s`
}

/** Live shape of a `GetCallLogListById` row (superset of CalldetailsLog). */
interface RawCallLog {
  id?: number
  callerid?: number
  dnid?: string
  call_start_time?: string
  callcreatedon?: string
  Date?: string
  callduration?: string
  Callduration?: string
  CustomerDuration?: string
  callstatus?: string
  Direction?: string
  direction?: string
  recordurl?: string
  RecodingUrl?: string
  agentname?: string
  AgentUser?: string
  AgentMode?: string
  user_login?: string
  Customernumber?: string
  MobileNo?: string
  LeadId?: number
  customerentity?: string
  customerentityid?: string
}

export async function fetchChatLogs(entityId: string, signal?: AbortSignal): Promise<ChatLogItem[]> {
  if (USE_MOCK) return mocked(mock.mockChatLogs)

  const res = await apiPost<RawChat[]>(ROUTES.tawkToChatLogs, { LeadId: toNumber(entityId) }, { signal })

  // Chatlog_VO carries the whole transcript in one `ChatLog` string rather than
  // a message list, so each row renders as one entry.
  return asArray(res)
    .filter((row) => (row.ChatLog ?? '').trim() !== '')
    .map((row, i) => ({
      id: String(row.LeadId ?? i),
      direction: 'in' as const,
      message: row.ChatLog ?? '',
      sentOn: '',
      sender: 'Chat transcript',
    }))
}

/** Mirrors Chatlog_VO in BO/MobileApp/MobileApp_BO.cs. */
interface RawChat {
  LeadId?: number
  ChatLog?: string
}

export async function fetchWebforms(entityId: string, signal?: AbortSignal): Promise<WebformItem[]> {
  if (USE_MOCK) return mocked(mock.mockWebforms)

  const res = await apiPost<RawWebform[]>(ROUTES.webforms, { LeadId: toNumber(entityId) }, { signal })

  return asArray(res).map((row, i) => ({
    id: String(row.EntryId ?? row.EntryNo ?? i),
    formName: row.FormName ?? '',
    submittedOn: row.EntryDate ?? row.ContactDate ?? '',
    submittedBy: row.Name ?? row.Email ?? '',
  }))
}

/** Mirrors WebformDetail_BO in BO/MobileApp/MobileApp_BO.cs. */
interface RawWebform {
  EntryId?: string
  EntryNo?: string
  FormName?: string
  EntryDate?: string
  ContactDate?: string
  Name?: string
  Email?: string
}

/* ---------------------------------------- task / appointment / call / phy */

export async function fetchTaskDetail(taskId: string, signal?: AbortSignal): Promise<TaskDetail | null> {
  if (USE_MOCK) return mocked(mock.mockTaskDetail)
  return await apiPost<TaskDetail | null>(ROUTES.taskDetail, { Id: toNumber(taskId) }, { signal })
}

export async function fetchPhysicalAppointment(
  appointmentId: string,
  signal?: AbortSignal,
): Promise<PhysicalAppointmentDetail | null> {
  if (USE_MOCK) return mocked(mock.mockPhysicalAppointment)

  const { userId } = getSessionContext()
  const res = await apiPost<PhysicalAppointmentDetail | PhysicalAppointmentDetail[] | null>(
    ROUTES.physicalAppointment,
    { PhysicalAppointmentId: toNumber(appointmentId), UserId: userId },
    { signal },
  )
  return Array.isArray(res) ? (res[0] ?? null) : res
}

export async function fetchCallLogDetail(
  callLogId: string,
  signal?: AbortSignal,
): Promise<CallLogDetail | null> {
  if (USE_MOCK) return mocked(mock.mockCallLogDetail)

  const { userId } = getSessionContext()
  const res = await apiPost<CallLogDetail | CallLogDetail[] | null>(
    ROUTES.callLogDetail,
    { Id: toNumber(callLogId), UserId: userId },
    { signal },
  )
  return Array.isArray(res) ? (res[0] ?? null) : res
}

export async function fetchInteractionHistories(
  leadId: string,
  signal?: AbortSignal,
): Promise<InteractionHistory[]> {
  if (USE_MOCK) return mocked(mock.mockInteractionHistories)

  const { timeZone } = getSessionContext()

  try {
    const res = await apiPost<InteractionHistory[]>(
      ROUTES.interactionHistories,
      { LeadId: toNumber(leadId), strTimezone: timeZone },
      { signal },
    )
    return asArray(res)
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    // UserCRM/GetIntractionHistories ships with this work; until the API is
    // deployed it 404s. Degrade to an empty list rather than failing the tab.
    console.warn(
      'UserCRM/GetIntractionHistories unavailable — deploy Kit19API_V2 to enable the follow-up history.',
      err,
    )
    return []
  }
}

export async function fetchUserFromHashedList(signal?: AbortSignal): Promise<HashedUser[]> {
  if (USE_MOCK) return mocked(mock.mockHashedUsers)
  const { userId } = getSessionContext()
  return (await apiPost<HashedUser[]>(ROUTES.userFromHashedList, { UserId: userId }, { signal })) ?? []
}

/* --------------------------------------------------------------- comments */

export async function fetchComments(
  taskId: string,
  variant: 'task' | 'physicalapt',
  signal?: AbortSignal,
): Promise<CommentItem[]> {
  if (USE_MOCK) return mocked(mock.mockComments)
  return (
    (await apiPost<CommentItem[]>(
      ROUTES.taskComments,
      { Id: toNumber(taskId), Mode: variant === 'physicalapt' ? 'physicalapt' : '' },
      { signal },
    )) ?? []
  )
}

/**
 * Saves a comment. Attachments are uploaded first through
 * `Common/UploadAttachment`; the returned names go to `SaveTaskComment`, which
 * takes `fileNames` rather than the file bodies.
 */
export async function addComment(params: {
  taskId: string
  taskType: string
  remarks: string
  files: File[]
}): Promise<void> {
  if (!params.remarks.trim()) {
    throw new Error('Please type some thing in comment text box')
  }

  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] SaveTaskComment', params)
    return
  }

  const { userId } = getSessionContext()
  const fileNames = params.files.length > 0 ? await uploadAttachments(params.files) : []

  await apiPost(ROUTES.saveTaskComment, {
    TaskId: toNumber(params.taskId),
    Remarks: params.remarks,
    CreatedBy: userId,
    fileNames,
  })
}

/** Multipart upload; returns the stored file names. */
async function uploadAttachments(files: File[]): Promise<string[]> {
  const body = new FormData()
  for (const file of files) body.append(file.name, file)

  const response = await fetch(`${API_BASE_URL}/${ROUTES.uploadAttachment}`, {
    method: 'POST',
    body,
  })
  if (!response.ok) throw new Error(`Attachment upload failed (${response.status})`)

  const text = await response.text()
  if (!text) return files.map((f) => f.name)

  try {
    const json = JSON.parse(text) as { Details?: unknown }
    const details = json.Details ?? json
    if (Array.isArray(details)) return details.map((d) => String((d as { FileName?: string }).FileName ?? d))
    return files.map((f) => f.name)
  } catch {
    return files.map((f) => f.name)
  }
}

/* ------------------------------------------------------------ WhatsApp log */

export async function fetchWaConversationUserId(
  entityName: 'lead' | 'enquiry',
  entityId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  if (USE_MOCK) return mocked('mock-conv-user')
  return await apiPost<string | null>(
    ROUTES.convUserId,
    { EntityName: entityName, EntityID: entityId },
    { signal },
  )
}

export async function fetchWaChatLog(
  entityName: 'lead' | 'enquiry',
  entityId: string,
  convUserId: string,
  signal?: AbortSignal,
): Promise<ChatLogItem[]> {
  if (USE_MOCK) return mocked(mock.mockWaChatLogs)

  const { userId } = getSessionContext()
  const res = await apiPost<RawWaMessage[]>(
    ROUTES.waConversation,
    {
      UserId: userId,
      EntityId: entityId,
      EntityName: entityName,
      ConversationId: Number(convUserId) || 0,
      LeadActivityId: 0,
      Direction: '',
    },
    { signal },
  )

  return asArray(res).map((row, i) => {
    const isOut = asText(row.Direction).toLowerCase().startsWith('out')
    return {
      id: String(row.Id ?? i),
      direction: (isOut ? 'out' : 'in') as ChatLogItem['direction'],
      message: asText(row.Text) || asText(row.SendMessageBody),
      sentOn: asText(row.CreatedOn),
      system: asText(row.Source).toLowerCase() === 'activity',
      messageType: asText(row.MessageType),
      // Outgoing messages are sent by the agent; incoming by the contact.
      // Sender/Recipient arrive as ConversationUser objects — asText picks PersonName.
      sender: isOut
        ? asText(row.AgentName) || asText(row.Sender)
        : asText(row.Sender) || asText(row.PersonEntityName) || asText(row.Recipient),
    }
  })
}

/** Live shape of an `IMSMOB/GetConversationAsRequiredConvId` row. */
interface RawWaMessage {
  Id?: string | number
  /** "conversation" for a real message, "activity" for the feed's own log lines. */
  Source?: string
  MessageType?: string
  Direction?: string
  Text?: string
  SendMessageBody?: string
  CreatedOn?: string
  /** ConversationUser object in the live API, not a string. */
  Sender?: unknown
  Recipient?: unknown
  AgentName?: string
  PersonEntityName?: string
}

/* ------------------------------------------------------------------ edit */

export async function fetchCountries(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockCountries)
  return toOptions(await apiPost<RawOption[]>(ROUTES.countries, {}, { signal }), 'CountryId', 'CountryName')
}

export async function fetchStates(countryId: string, signal?: AbortSignal): Promise<Option[]> {
  if (!countryId) return []
  if (USE_MOCK) return mocked(mock.mockStates[countryId] ?? [], 120)
  return toOptions(
    await apiPost<RawOption[]>(ROUTES.states, { CountryId: toNumber(countryId) }, { signal }),
    'StateId',
    'StateName',
  )
}

export async function fetchCities(stateId: string, signal?: AbortSignal): Promise<Option[]> {
  if (!stateId) return []
  if (USE_MOCK) return mocked(mock.mockCities[stateId] ?? [], 120)
  return toOptions(
    await apiPost<RawOption[]>(ROUTES.cities, { StateId: toNumber(stateId) }, { signal }),
    'CityId',
    'CityName',
  )
}

export async function fetchSources(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockSources)
  const { userId } = getSessionContext()
  // Inactive sources included so a lead whose stored source is retired can
  // still resolve its name to the id usp_EditLeadDetails needs.
  const res = await apiPost<RawOption[]>(
    ROUTES.sources,
    { SourceName: '', IsActive: true, IsInactive: true, UserId: userId },
    { signal },
  )
  // Live shape is { SourceID, Name, IsActive }. usp_EditLeadDetails takes the
  // numeric id (@SourceName is bigint), so the option id must be SourceID.
  return dedupeOptions(
    asArray(res).map((row) => ({
      id: firstValue(row, 'SourceID', 'SourceId', 'Id', 'Code'),
      name: firstValue(row, 'Name', 'SourceName', 'Text'),
    })),
  )
}

export async function fetchMediums(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockMediums)
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.mediums, { ParentId: parentId }, { signal })
  // Rows are { Id: 0, Code: "<mediumId>", Text: "<name>" } — the id is in Code.
  return toOptions(res, 'Code', 'Text')
}

export async function fetchCampaigns(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockCampaigns)
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.campaigns, { ParentId: parentId }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/**
 * Editable custom fields.
 *
 * `UserCRM/EntityEditGetCustomFields` is part of this work and may not be
 * deployed to the target server yet. When it is missing, the name/value pairs
 * that come back inside the entity detail response are used instead, so the
 * section still renders (as plain text fields) rather than the form breaking.
 */
export async function fetchEditCustomFields(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  signal?: AbortSignal,
): Promise<CustomFieldDef[]> {
  if (USE_MOCK) return mocked(mock.mockEditCustomFields)

  const { parentId } = getSessionContext()

  try {
    const res = await apiPost<CustomFieldDef[] | string | null>(
      ROUTES.entityEditCustomFields,
      { entityType, entityId, ParentId: parentId },
      { signal },
    )
    const defs = !res ? [] : typeof res === 'string' ? (JSON.parse(res) as CustomFieldDef[]) : res
    if (defs.length > 0) return defs
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    console.warn(
      'UserCRM/EntityEditGetCustomFields unavailable (deploy the API to enable typed custom fields); ' +
        'falling back to the values in the entity detail response.',
      err,
    )
  }

  return fallbackCustomFieldDefs(entityType, entityId, signal)
}

/** Turns the detail response's { fieldid, fieldname, fieldvalue } into text fields. */
async function fallbackCustomFieldDefs(
  entityType: 'lead' | 'enquiry',
  entityId: string,
  signal?: AbortSignal,
): Promise<CustomFieldDef[]> {
  const pairs =
    entityType === 'lead'
      ? ((await loadLeadDetail(entityId, signal))?.CustomFields ?? [])
      : ((await loadEnquiryDetail(entityId, signal))?.CustomFields ?? [])

  return pairs.map((pair) => ({
    field_ID: Number(pair.fieldid),
    fieldname: pair.fieldname,
    datatype: 'Text' as const,
    fieldvalue: pair.fieldvalue ?? '',
    choices: '',
    isMandatory: false,
    minvalue: '',
    maxvalue: '',
    defaultValue: '',
  }))
}

/**
 * Dependency rules for custom fields whose choices depend on a parent field
 * (`UserCRM/GetDependentFieldData`). Port of `jsEntityEditObj.getDependentData`.
 */
export async function fetchDependentFieldData(
  fieldIds: Array<number | string>,
  signal?: AbortSignal,
): Promise<DependentFieldData[]> {
  if (fieldIds.length === 0) return []
  if (USE_MOCK) return mocked(mock.mockDependentFields, 120)

  const { parentId } = getSessionContext()
  return (
    (await apiPost<DependentFieldData[]>(
      ROUTES.dependentFieldData,
      { ParentId: parentId, CustomFieldIds: fieldIds.join(',') },
      { signal },
    )) ?? []
  )
}

/** Mirrors DepedentFeildDetail_BO in BO/UserCRM/EntityEdit_BO.cs. */
export interface DependentFieldData {
  FieldId: string
  Choices: string
  FormulaId: string
  ParentFieldId: string
  /** JSON: `[{ "ParentValue": "...", "ChildValue": ["...", "..."] }]` */
  DependencyJson: string
  DefaultValue: string
}

/** One parent value and the child choices it unlocks. */
export interface DependencyRule {
  ParentValue: string
  ChildValue: string[]
}

/** Builds fieldId → rules, so a child's options can be derived from its parent. */
export function buildDependencyMap(rows: DependentFieldData[]): Map<number, DependencyRule[]> {
  const map = new Map<number, DependencyRule[]>()

  for (const row of rows) {
    const fieldId = Number(row.FieldId)
    if (!fieldId || !row.DependencyJson) continue
    try {
      const parsed = JSON.parse(row.DependencyJson) as DependencyRule[]
      if (Array.isArray(parsed) && parsed.length > 0) map.set(fieldId, parsed)
    } catch {
      // A malformed rule must not break the form; the field keeps its full choice list.
    }
  }

  return map
}

export async function saveLead(payload: LeadSavePayload): Promise<string> {
  if (USE_MOCK) {
    await delay(400)
    console.info('[mock] EditLead payload', payload)
    return 'Lead updated successfully.'
  }

  const { parentId, userId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.editLead, {
    ...payload,
    LeadId: toNumber(payload.LeadId),
    parentId,
    userId,
  })
  invalidateLeadDetail()
  return typeof result === 'string' && result ? result : 'Saved successfully.'
}

export async function saveEnquiry(payload: EnquirySavePayload): Promise<string> {
  if (USE_MOCK) {
    await delay(400)
    console.info('[mock] EditEnquiry payload', payload)
    return 'Enquiry updated successfully.'
  }

  const { userId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.editEnquiry, {
    ...payload,
    EnquiryId: toNumber(payload.EnquiryId),
    UserId: userId,
  })
  return typeof result === 'string' && result ? result : 'Saved successfully.'
}

/**
 * Single-column save behind the pencil icon on each edit row.
 *
 * Exactly the legacy `SaveInlineData` contract: `FieldId` is 0 for standard
 * columns (the custom-field id for custom fields) and `FilterExp` is always
 * `LeadId=<entityId>` — usp_mk_UpdateField builds its WHERE clause from it and
 * errors out when the filter is empty.
 */
export async function saveInlineField(params: {
  entityId: string
  tableName: string
  fieldName: string
  fieldValue: string
  /** Custom-field id; omit for standard columns. */
  customFieldId?: string
}): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] SaveInlineEdit', params)
    return 'Data Saved Sucessfully'
  }

  const { userId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.saveInlineEdit, {
    UserId: userId,
    FieldId: params.customFieldId ?? '0',
    FieldName: params.fieldName,
    FieldValue: params.fieldValue,
    TableName: params.tableName,
    FilterExp: `LeadId=${toNumber(params.entityId)}`,
  })
  invalidateLeadDetail()
  return typeof result === 'string' && result ? result : 'Saved.'
}

/* ---------------------------------------------------- profile image upload */

const IMAGE_EXTENSIONS = ['png', 'jpeg', 'jpg', 'gif']
const MAX_IMAGE_BYTES = 1048576

/** Keeps the legacy guards: image types only, and under 1 MB. */
export async function uploadProfileImage(params: {
  entityType: string
  entityId: string
  file: File
}): Promise<string> {
  const ext = params.file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!IMAGE_EXTENSIONS.includes(ext)) {
    throw new Error('Please select a valid image file.')
  }
  if (params.file.size >= MAX_IMAGE_BYTES) {
    throw new Error('Image file size must be less then 1MB')
  }

  if (USE_MOCK) {
    await delay(400)
    return URL.createObjectURL(params.file)
  }

  const { parentId, userId } = getSessionContext()
  const body = new FormData()
  body.append('hdnHandlerMode', 'EntityProfile')
  body.append('CreatedBy', String(userId))
  body.append('ParentId', String(parentId))
  body.append('EntityName', params.entityType)
  body.append('EntityId', params.entityId)
  body.append(params.file.name, params.file)

  const response = await fetch(`${API_BASE_URL}/${ROUTES.uploadAttachment}`, {
    method: 'POST',
    body,
  })
  if (!response.ok) throw new Error('Attachment not Added!')

  const text = await response.text()
  if (!text) throw new Error('Attachment not Added!')

  const json = JSON.parse(text) as { Details?: unknown; url?: string; is_saved?: boolean }
  const details = (json.Details ?? json) as { url?: string; FileUrl?: string; is_saved?: boolean }
  const url = details.url ?? details.FileUrl ?? json.url

  if (!url) throw new Error('Attachment not Added!')
  invalidateLeadDetail()
  return url
}

/* --------------------------------------------------- permissions & masking */

const ALL_ALLOWED: PermissionMap = {
  Add: true,
  Edit: true,
  View: true,
  Delete: true,
  Search: true,
  Import: true,
  Export: true,
  Download: true,
  Print: true,
  AddTask: true,
  AddAppointment: true,
  AddFollowup: true,
  AddNote: true,
  SendSms: true,
  SendMail: true,
  SendVoice: true,
  SendWhatsapp: true,
  UploadDocument: true,
  DeleteDocument: true,
  AddDeal: true,
  AddWebform: true,
  AddCustomField: true,
  AddTax: true,
  AddQuotation: true,
  AddInvoice: true,
}

/** Module codes the legacy pages resolved from usp_tbl_UA_ModuleMasterGetModuleCodeByURL. */
export const MODULE_CODES = {
  lead: 'PAG10031', // LeadDetails.aspx — same code the Sales module uses
  enquiry: 'PAG10002', // Enquiry.aspx
} as const

/**
 * Role permissions — same source and semantics as the Kit19Sales module:
 * UserAuth/GetAllPermissionByModuleCodeUserId returns one row per module with
 * boolean action flags, and `!== false` means allowed (a missing flag never
 * hides a button). Any failure resolves to "everything allowed": the legacy
 * code also left the UI untouched when the permission call failed.
 */
export async function fetchPermissions(
  moduleCode?: string,
  signal?: AbortSignal,
): Promise<PermissionMap> {
  if (USE_MOCK) return mocked(ALL_ALLOWED, 100)

  const session = getSessionContext()
  const code = moduleCode || session.moduleCode || MODULE_CODES.lead

  try {
    const res = await apiPost<unknown>(
      ROUTES.permissions,
      { Mode: 'S', ModuleCode: code, UserId: session.userId },
      { signal },
    )

    // The API answers with an array of module rows (possibly JSON-encoded);
    // a bare object is tolerated for older deployments.
    let rows: RawOption[] = []
    if (Array.isArray(res)) rows = res as RawOption[]
    else if (typeof res === 'string') {
      try {
        const parsed = JSON.parse(res)
        rows = Array.isArray(parsed) ? parsed : [parsed]
      } catch {
        rows = []
      }
    } else if (res && typeof res === 'object') rows = [res as RawOption]

    if (rows.length === 0) return ALL_ALLOWED

    const row = rows.find((r) => String(r.ModuleCode ?? '') === code) ?? rows[0]
    const allowed = (key: string) => row[key] !== false

    const map = {} as PermissionMap
    for (const key of Object.keys(ALL_ALLOWED) as Array<keyof PermissionMap>) {
      map[key] = allowed(key)
    }
    return map
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    console.warn('Permission lookup failed; leaving all actions enabled.', err)
    return ALL_ALLOWED
  }
}

export async function fetchFieldMasks(signal?: AbortSignal): Promise<FieldMask[]> {
  if (USE_MOCK) return mocked([], 80)

  const { parentId, userId, moduleCode } = getSessionContext()
  if (!moduleCode) return []

  const res = await apiPost<RawFieldMask[] | null>(
    ROUTES.fieldMasked,
    { ParentId: parentId, UserId: userId, PageCode: moduleCode },
    { signal },
  )

  return asArray(res).map((row) => ({
    FieldName: row.FieldName ?? '',
    HiddenType: (row.HiddenType ?? '').toLowerCase(),
    TableName: row.TableName,
  }))
}

interface RawFieldMask {
  FieldName?: string
  HiddenType?: string
  TableName?: string
}

/* ----------------------------------------------------------------- helpers */

interface RawOption {
  [key: string]: unknown
}

/** Normalises a lookup list to { id, name }, tolerating column-name variants. */
function toOptions(rows: RawOption[] | null | undefined, idKey: string, nameKey: string): Option[] {
  return dedupeOptions(
    asArray(rows).map((row) => {
      const id = firstValue(row, idKey, 'Id', 'Value', 'Code')
      const name = firstValue(row, nameKey, 'Text', 'Name', 'Title')
      return { id: id || name, name: name || id }
    }),
  )
}

/**
 * Drops blanks and duplicate ids. The live source/medium/campaign lists run to
 * hundreds of rows with repeats, which would otherwise produce duplicate
 * <option> values and React key warnings.
 */
function dedupeOptions(options: Option[]): Option[] {
  const seen = new Set<string>()
  const result: Option[] = []

  for (const option of options) {
    if (!option.name || seen.has(option.id)) continue
    seen.add(option.id)
    result.push(option)
  }

  return result
}

function firstValue(row: RawOption, ...keys: string[]): string {
  const lowered = new Map<string, unknown>()
  for (const [key, value] of Object.entries(row)) lowered.set(key.toLowerCase(), value)

  for (const key of keys) {
    const value = lowered.get(key.toLowerCase())
    if (value != null && String(value).trim() !== '') return String(value).trim()
  }
  return ''
}

/** Exposed so the landing page can show which API it is pointed at. */
export function getApiInfo() {
  return { baseUrl: API_BASE_URL, hasToken: API_TOKEN !== '', useMock: USE_MOCK }
}

/* ---------------------------------------------------- action widget lookups */

export async function fetchFollowupStatuses(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockFollowupStatuses)
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.followupStatusList, { ParentId: parentId }, { signal })
  return toOptions(res, 'Id', 'Text')
}

export async function fetchAssignedUsers(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockUsers)
  const { parentId } = getSessionContext()
  // GetUserParentIDWise deserializes Details as a raw number, not an object.
  // Rows come back as { Id: 0, Code: "<userId>", Text: "<name>" } — the id
  // lives in Code, and Id would win as the string "0", so map Code first.
  const res = await apiPost<RawOption[]>(ROUTES.usersList, parentId, { signal })
  return toOptions(res, 'Code', 'Text')
}

export async function fetchProductList(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockProducts)
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.productsList, { UserId: userId }, { signal })
  return toOptions(res, 'Id', 'Text')
}

/**
 * Port of the followup form's product autocomplete
 * (LeadDetails.aspx/GetProductsByName). Legacy sent hdnSipPid as stUserID.
 */
export async function fetchProductSuggestions(nameFilter: string, signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockProducts)
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(
    ROUTES.productsByName,
    { stName: nameFilter, stUserID: parentId },
    { signal },
  )
  return toOptions(res, 'Id', 'Text')
}

/** Port of IMSService/getSmsSenderIds → proc_displaySenderNameOfGSMType_DLT. */
export async function fetchSmsSenderIds(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockSmsSenderIds)
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.smsSenderIds, { AccountId: 0, UserId: userId }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/**
 * Port of IMSService/getSmsTemplates → Usp_GetDLTTemplateBySenderName. The
 * legacy form reloaded this list every time the sender changed; option ids are the
 * DLT template ids.
 */
export async function fetchSmsTemplates(senderId?: string, signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockSmsTemplates)
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(
    ROUTES.smsTemplates,
    { SenderName: senderId ?? '', UserId: userId },
    { signal },
  )
  return toOptions(res, 'Code', 'Text')
}

/** Port of CRMService/Get_DLTTemplateText — template body + unicode flag. */
export async function fetchDltTemplateDetails(
  templateId: string,
  signal?: AbortSignal,
): Promise<{ text: string; isUnicode: boolean }> {
  if (USE_MOCK) return mocked({ text: 'Mock DLT template text', isUnicode: false })
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[] | RawOption | null>(
    ROUTES.dltTemplateDetails,
    { DLTTemplateId: templateId, UserId: userId },
    { signal },
  )
  const row = (Array.isArray(res) ? res[0] : res) ?? {}
  return {
    text: String(firstValue(row, 'TemplateText', 'Text', 'Message') ?? ''),
    isUnicode: String(firstValue(row, 'IsUnicode', 'Unicode') ?? '').toLowerCase() === 'true',
  }
}

/**
 * Port of CRMService/ValidateDLTTemplateText — the pre-send compliance check.
 * Legacy treated '0' as "template text does not match".
 */
export async function validateDltTemplate(templateId: string, message: string): Promise<boolean> {
  if (USE_MOCK) return mocked(true)
  const res = await apiPost<string | number | null>(ROUTES.validateDltTemplate, {
    DLTTemplateID: templateId,
    Message: message,
  })
  return String(res ?? '').trim() !== '0'
}

/** Port of TriggerCampaign/FuncToGetEntityPlaceHolder — merge-token list. */
export async function fetchEntityPlaceholders(
  entityType: 'lead' | 'enquiry',
  signal?: AbortSignal,
): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(
    ROUTES.entityPlaceholders,
    { strEntityName: entityType, strParentID: String(parentId) },
    { signal },
  )
  return toOptions(res, 'Code', 'Text')
}

/** NEW endpoint (port of IMSService/GetDltTemplateStatus) — reminder-SMS gating. */
export async function fetchDltTemplateStatus(
  mode: string,
  signal?: AbortSignal,
): Promise<{ isRegistered: boolean; dltTemplateId: string; message: string }> {
  if (USE_MOCK) return mocked({ isRegistered: true, dltTemplateId: '', message: '' })
  const { userId } = getSessionContext()
  const res = await apiPost<{ IsRegistered?: boolean; DLTTemplateId?: string; Message?: string } | null>(
    ROUTES.dltTemplateStatus,
    { UserId: userId, Mode: mode },
    { signal },
  )
  return {
    isRegistered: res?.IsRegistered === true,
    dltTemplateId: res?.DLTTemplateId ?? '',
    message: res?.Message ?? '',
  }
}

/** Port of IMSService/getEmailTemplateList → usp_GetUserMailTemplateBind. */
export async function fetchEmailTemplates(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockEmailTemplates)
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(
    ROUTES.emailTemplates,
    { ParentID: parentId, isSMS: false, isMail: true },
    { signal },
  )
  return toOptions(res, 'Id', 'Text')
}

/** Port of CRMService/funcToGetFromMailID — From / Reply-To dropdown. */
export async function fetchFromMailIds(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.fromMailIds, { ParentId: parentId }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of TriggerCampaign/FuncToGetTemplateSubject — subject autofill. */
export async function fetchMailTemplateSubject(templateId: string, signal?: AbortSignal): Promise<string> {
  if (USE_MOCK) return mocked('')
  const res = await apiPost<string | RawOption | null>(
    ROUTES.mailTemplateSubject,
    { TemplateId: toNumber(templateId) },
    { signal },
  )
  if (!res) return ''
  if (typeof res === 'string') return res
  return String(firstValue(res, 'Subject', 'Text') ?? '')
}

/** Port of IMSService/GetDIDNo — voice DNID dropdown. */
export async function fetchDidNumbers(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.didNumbers, { ParentId: parentId }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of IMSService/getAppName — voice IVR app-flow dropdown. */
export async function fetchAppNames(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.appNames, { ParentId: parentId }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of IMSService/getDealPipeline. Details is the raw ParentId number. */
export async function fetchDealPipelines(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.dealPipelines, parentId, { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of IMSService/getDealPipelineStage. Details is the raw PipelineId number. */
export async function fetchDealPipelineStages(pipelineId: string, signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const res = await apiPost<RawOption[]>(ROUTES.dealPipelineStages, toNumber(pipelineId), { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of IMSService/getDealPipelineStageReason — lost-reason dropdown. */
export async function fetchDealStageReasons(pipelineId: string, signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const res = await apiPost<RawOption[]>(ROUTES.dealPipelineStageReasons, { PipelineId: toNumber(pipelineId) }, { signal })
  return toOptions(res, 'Code', 'Text')
}

/** Port of ToDoService/GetPredefinedOutcomeListByUserId. */
export async function fetchPredefinedOutcomes(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.predefinedOutcomes, { UserId: userId }, { signal })
  return toOptions(res, 'Id', 'Text')
}

/** Port of ToDoService/GetOutcomeListByTaskId — outcomes for one task type. */
export async function fetchOutcomesByTaskType(taskTypeId: string, signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(
    ROUTES.outcomesByTaskType,
    { TaskId: toNumber(taskTypeId), UserId: userId },
    { signal },
  )
  return toOptions(res, 'Id', 'Text')
}

/** Task types for the Add Task form. */
export async function fetchTaskTypes(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked([])
  const { parentId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.taskTypes, { ParentId: parentId }, { signal })
  return toOptions(res, 'Id', 'Text')
}

/** Port of CRMService/GetUserWebformListByUserId → ups_wf_GetWebFormByUserId. */
export async function fetchUserWebforms(signal?: AbortSignal): Promise<Option[]> {
  if (USE_MOCK) return mocked(mock.mockActionWebforms)
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[]>(ROUTES.userWebforms, { UserId: userId }, { signal })
  return toOptions(res, 'Id', 'Text')
}

/* --------------------------------------------------------------- whatsapp */

/**
 * Port of InvoiceService/IsLeadRegisterForWhatsapp — returns the entity's
 * ChannelProfileId ('' when the number is not yet bound to WhatsApp).
 */
export async function fetchWaRegistration(
  entityId: string,
  entityType: 'lead' | 'enquiry',
  signal?: AbortSignal,
): Promise<string> {
  if (USE_MOCK) return mocked('')
  const res = await apiPost<string | { ChannelProfileId?: string } | null>(
    ROUTES.waRegistration,
    { LeadId: toNumber(entityId), EntityName: entityType },
    { signal },
  )
  if (!res) return ''
  if (typeof res === 'string') {
    try {
      const parsed = JSON.parse(res)
      return parsed?.ChannelProfileId ?? ''
    } catch {
      return ''
    }
  }
  return res.ChannelProfileId ?? ''
}

/**
 * Port of IMSService/GetChannelTemplateListByUserId (Mode 'approved').
 *
 * The `_ByParam` route answers with the conversation-DB column names
 * (`templatename`, `tlanguage`, `channelprofileid`, `kit19TemplateJSON`), so the
 * rows are normalised here to the PascalCase shape the form and the send
 * payload use — otherwise every template renders as "Template n" and the send
 * goes out with an empty name.
 */
export async function fetchWaTemplates(signal?: AbortSignal): Promise<WaChannelTemplate[]> {
  if (USE_MOCK) return mocked([])
  const { userId } = getSessionContext()
  const res = await apiPost<RawOption[] | null>(
    ROUTES.waChannelTemplates,
    { usid: userId, mode: 'approved' },
    { signal },
  )

  return asArray(res).map((row) => ({
    ...row,
    Id: Number(firstValue(row, 'Id', 'id')) || undefined,
    TemplateName: firstValue(row, 'TemplateName', 'templatename'),
    TemplateNamespace: firstValue(row, 'TemplateNamespace', 'namespace', 'templatenamespace'),
    LanguageCode: firstValue(row, 'LanguageCode', 'tlanguage', 'language') || 'en',
    Category: firstValue(row, 'Category', 'category'),
    TemplateJson: firstValue(row, 'TemplateJson', 'kit19TemplateJSON', 'templatejson'),
    ChannelProfileId: firstValue(row, 'ChannelProfileId', 'channelprofileid'),
  }))
}

/** Port of IMSService/GetActiveApiKey — the key the WA send payload carries. */
export async function fetchWaApiKey(signal?: AbortSignal): Promise<string> {
  if (USE_MOCK) return mocked('mock-key')
  const { userId } = getSessionContext()
  const res = await apiPost<string | null>(ROUTES.waActiveApiKey, { UserId: userId }, { signal })
  return typeof res === 'string' ? res : ''
}

/** jsWaEntityMangager — candidate numbers that can be bound to this entity. */
export async function fetchWaAssociatedNumbers(
  entityId: string,
  entityType: 'lead' | 'enquiry',
  signal?: AbortSignal,
): Promise<Array<{ MobileNo?: string; ConvUserId?: number; EntityId?: number; EntityName?: string }>> {
  if (USE_MOCK) return mocked([])
  const res = await apiPost<Array<{ MobileNo?: string }> | null>(
    ROUTES.waAssociatedMobileNos,
    { EntityId: toNumber(entityId), EntityName: entityType },
    { signal },
  )
  return asArray(res)
}

/** jsWaEntityMangager — associate (or move) a WhatsApp number onto this entity. */
export async function associateWaNumber(args: {
  entityId: string
  entityType: 'lead' | 'enquiry'
  mobileNo: string
  oldEntityId?: string
  oldEntityName?: string
}): Promise<{ status?: string; message?: string }> {
  if (USE_MOCK) return mocked({ status: 'success', message: 'Associated.' })
  const { userId } = getSessionContext()
  const res = await apiPost<{ status?: string; message?: string } | null>(ROUTES.waAssociate, {
    oldEntityName: args.oldEntityName ?? '',
    oldEntityId: args.oldEntityId ? toNumber(args.oldEntityId) : 0,
    EntityId: toNumber(args.entityId),
    EntityName: args.entityType,
    ChannelName: 'whatsapp',
    ChannelProfileId: args.mobileNo.replace(/\D/g, ''),
    profileJson: '{}',
    UserId: userId,
  })
  return res ?? {}
}

/* ---------------------------------------------------- action widget submits */

/**
 * Port of CRMService/AddLeadFollowup_DLT — the exact save the legacy edit
 * page's followup action ran, reminder SMS/email included.
 */
export async function submitFollowup(payload: FollowupPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitFollowup', payload)
    return 'Followup saved successfully.'
  }
  const { userId, parentId, timeZone } = getSessionContext()
  const result = await apiPost<string | { Msg?: string }>(ROUTES.addFollowup, {
    objLeadFollowup_BO: {
      LeadId: toNumber(payload.entityId),
      AssignedTo: Number(payload.assignedUser) || 0,
      ParentID: parentId,
      FollowUpStatus: Number(payload.followupStatus) || 0,
      NextStatusDate: payload.followupDate,
      Remarks: payload.remarks,
      Products: payload.selectedProducts.join(','),
      AmountPaid: payload.amountPaid,
      CreatedBy: userId,
      IsReAssign: payload.reAssign,
      NotifyBySMS: payload.notifyBySms,
      NotifyByEmail: payload.notifyByEmail,
      SmsScheduelDateTime: payload.smsScheduleDateTime,
      EmailScheduelDateTime: payload.emailScheduleDateTime,
    },
    strDLTTemplateID: payload.dltTemplateId,
    strUserID: String(userId),
    strTimeZone: timeZone || 'India Standard Time',
    strParentID: String(parentId),
  })
  invalidateLeadDetail()
  if (result && typeof result === 'object' && 'Msg' in result) return result.Msg || 'Followup saved successfully.'
  return typeof result === 'string' && result ? result : 'Followup saved successfully.'
}

/** Port of CRMService/SendSms with the legacy objSendSms_BO shape. */
export async function submitSms(payload: SmsPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitSms', payload)
    return 'Sms Sent Successfully'
  }
  const { userId, parentId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.sendSms, {
    objSendSms: {
      SenderID: payload.senderId,
      SenderName: payload.senderId,
      MobileNo: payload.mobileNo,
      MobileNo1: payload.mobileNo1,
      MobileNo2: payload.mobileNo2,
      Message: payload.message,
      Template: payload.templateId,
      IsUnicode: payload.isUnicode,
      EntityType: payload.entityType,
      EnquiryLeadId: toNumber(payload.entityId),
    },
    ParentId: parentId,
    UserId: userId,
  })
  return typeof result === 'string' && result ? result : 'Sms Sent Successfully'
}

/** Port of CRMService/SendEmail with the legacy objSendEmail_BO shape. */
export async function submitEmail(payload: EmailPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitEmail', payload)
    return 'Email sent successfully.'
  }
  const { userId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.sendEmail, {
    objSendEmail: {
      Email: payload.email,
      Email1: payload.email1,
      Email2: payload.email2,
      Subject: payload.subject,
      Message: payload.body,
      Template: payload.templateId,
      EmailType: payload.emailType,
      EntityType: payload.entityType,
      ReplyTo: payload.replyTo,
      FromMailID: payload.fromMailId,
      EnquiryLeadId: toNumber(payload.entityId),
    },
    userId: userId,
  })
  return typeof result === 'string' && result ? result : 'Email sent successfully.'
}

/** Port of CRMService/SendVoiceEnquiry with the legacy objSendVoice_BO shape. */
export async function submitVoice(payload: VoicePayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitVoice', payload)
    return 'Voice call triggered successfully.'
  }
  const { parentId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.sendVoice, {
    SendVoice: {
      MobileNo: payload.mobileNo,
      MobileNo1: payload.mobileNo1,
      MobileNo2: payload.mobileNo2,
      Route: payload.route,
      AppFlow: payload.appFlow,
      DnidNo: payload.dnidNo,
      Duration: payload.duration,
      Voice_Source: payload.voiceSource,
      EntityName: payload.entityType,
      EntityId: payload.entityId,
    },
    ParentId: parentId,
  })
  return typeof result === 'string' && result ? result : 'Voice broadcast initiated.'
}

/**
 * Port of the jsInvWA template send — posts the same JObject the legacy page
 * posted to IMS/Whatsapp/Template, key and all.
 */
export async function submitWhatsapp(payload: WhatsappPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitWhatsapp', payload)
    return 'WhatsApp message sent successfully.'
  }
  const { userId } = getSessionContext()
  const key = await fetchWaApiKey()
  const template = payload.template
  const to = payload.mobileNo.replace(/\D/g, '')

  const body = {
    key,
    username: String(userId),
    name: 'whatsapp',
    remarks: payload.remarks ?? '',
    whatsapp: {
      to,
      type: 'template',
      category: template.Category ?? '',
      recipient_type: 'individual',
      template: {
        namespace: template.TemplateNamespace ?? '',
        language: { policy: 'deterministic', code: template.LanguageCode || 'en' },
        name: template.TemplateName ?? '',
        components: [],
      },
    },
  }

  const res = await apiPostRaw<{ errors?: Array<{ code?: number; title?: string }>; meta?: { developer_message?: string } }>(
    ROUTES.waSendTemplate,
    body,
  )

  const errors = res?.errors
  if (Array.isArray(errors) && errors.length > 0) {
    if (errors[0]?.code === 1006) {
      throw new Error('This number is not associated with a WhatsApp channel yet. Associate it first.')
    }
    throw new Error(errors[0]?.title || res?.meta?.developer_message || 'WhatsApp send failed.')
  }
  return 'WhatsApp message sent successfully.'
}

/** Port of CRMService/AddNotes → usp_InsertUpdateLeadNotes. */
export async function submitNote(payload: NotePayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitNote', payload)
    return 'Note added successfully.'
  }
  const { userId, parentId } = getSessionContext()
  const result = await apiPost<string | { Msg?: string }>(ROUTES.addNote, {
    LeadID: toNumber(payload.entityId),
    Notes: payload.noteText,
    ParentId: parentId,
    UserId: userId,
  })
  invalidateLeadDetail()
  if (result && typeof result === 'object' && 'Msg' in result) return result.Msg || 'Note added successfully.'
  return typeof result === 'string' && result ? result : 'Note added successfully.'
}

/**
 * Port of CRMService/GetInitalRemarksOnly — the note text the legacy enquiry
 * "Add Notes" action edited (enquiries store one running note, not a list).
 */
export async function fetchInitialRemarks(
  entityId: string,
  entityType: 'lead' | 'enquiry',
  signal?: AbortSignal,
): Promise<string> {
  if (USE_MOCK) return mocked('')
  const res = await apiPost<string | null>(
    ROUTES.initialRemarks,
    { entityName: entityType, entityId: toNumber(entityId) },
    { signal },
  )
  return typeof res === 'string' ? res : ''
}

/** Port of CRMService/UpdateIntialRemarks. */
export async function updateInitialRemarks(
  entityId: string,
  entityType: 'lead' | 'enquiry',
  remarks: string,
): Promise<boolean> {
  if (USE_MOCK) {
    await delay(200)
    return true
  }
  const res = await apiPost<boolean | string | null>(ROUTES.updateInitialRemarks, {
    entityId: toNumber(entityId),
    entityName: entityType,
    Remarks: remarks,
  })
  invalidateEnquiryDetail()
  return res === true || String(res).toLowerCase() === 'true'
}

/**
 * Multipart contract of UserCRM/UploadLeadDocument (the REST replacement for
 * LeadHandler.ashx / ImsLeadDocument): a form field `details` carrying
 * { Token, fileUpload: { ParentId, CreatedBy, LeadId } } as JSON, plus the
 * file in a part named `file`. UploadLeadDocument**s** (plural) binds JSON,
 * not multipart — posting FormData there fails.
 */
export async function submitDocument(payload: DocumentPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitDocument', payload)
    return 'Document uploaded successfully.'
  }
  if (!payload.file) throw new Error('Please choose a file to upload.')

  const { userId, parentId } = getSessionContext()
  const body = new FormData()
  body.append(
    'details',
    JSON.stringify({
      Token: Number(API_TOKEN),
      fileUpload: {
        ParentId: String(parentId),
        CreatedBy: String(userId),
        LeadId: toNumber(payload.entityId),
      },
    }),
  )
  body.append('file', payload.file, payload.file.name)

  const response = await fetch(`${API_BASE_URL}/${ROUTES.uploadDoc}`, { method: 'POST', body })
  const data = await response.json().catch(() => null)
  if (!response.ok || (data && data.Status !== 1)) {
    throw new Error(data?.Message === 'Error' ? String(data?.Details ?? 'Failed to upload document') : (data?.Message ?? 'Failed to upload document'))
  }
  invalidateLeadDetail()
  return 'Document uploaded successfully.'
}

/**
 * Port of Appointment.aspx/InsertNewTaskOrAppointment with IsTask=true — the
 * same insertUpdateTask BO the legacy Add Task form posted.
 */
export async function submitActionTask(payload: ActionTaskPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitActionTask', payload)
    return 'Task created successfully.'
  }
  const result = await apiPost<string | { Msg?: string }>(
    ROUTES.addTaskOrAppointment,
    buildTaskBo({ ...payload, location: '', completeAddress: '' }, { isTask: true }),
  )
  invalidateLeadDetail()
  if (result && typeof result === 'object' && 'Msg' in result) return result.Msg || 'Task created successfully.'
  return typeof result === 'string' && result ? result : 'Task created successfully.'
}

/** Same BO with IsAppointment=true, as the legacy Add Appointment form posted. */
export async function submitActionAppointment(payload: ActionAppointmentPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitActionAppointment', payload)
    return 'Appointment scheduled successfully.'
  }
  const result = await apiPost<string | { Msg?: string }>(
    ROUTES.addTaskOrAppointment,
    buildTaskBo({ ...payload, taskType: '', outcomesId: '' }, { isTask: false }),
  )
  invalidateLeadDetail()
  if (result && typeof result === 'object' && 'Msg' in result) return result.Msg || 'Appointment scheduled successfully.'
  return typeof result === 'string' && result ? result : 'Appointment scheduled successfully.'
}

interface TaskBoInput {
  entityId: string
  title: string
  description: string
  taskType: string
  outcomesId: string
  location?: string
  completeAddress?: string
  startDate: string
  endDate: string
  assignedTo: string
  collaboratorIds: string
  remarks: string
  notifyBySms: boolean
  notifyByEmail: boolean
}

/** The insertUpdateTask BO both legacy forms shared (InsertUpdateTask_BO). */
function buildTaskBo(input: TaskBoInput, kind: { isTask: boolean }) {
  const { userId } = getSessionContext()
  return {
    Id: 0,
    Title: input.title,
    Description: input.description,
    Remarks: input.remarks,
    Address: input.location ?? '',
    CompleteAddress: input.completeAddress ?? '',
    StartDate: input.startDate || null,
    EndDate: input.endDate || null,
    LeadId: toNumber(input.entityId),
    TaskId: 0,
    TaskType: input.taskType,
    OutcomesId: Number(input.outcomesId) || 0,
    AssignedTo: Number(input.assignedTo) || userId,
    CollabratoerUserIds: input.collaboratorIds,
    CollaboratorUserIds: input.collaboratorIds,
    IsEmail: input.notifyByEmail,
    IsSMS: input.notifyBySms,
    IsTask: kind.isTask,
    IsAppointment: !kind.isTask,
    IsActive: true,
    IsCompleted: false,
    CreatedBy: userId,
    NotifyMe: false,
    Latitude: '',
    Longitude: '',
    SmsSchedule: '',
    EmailSchedule: '',
    DLTTemplateIDForSMSReminder: '',
    DLTTemplateIDForNotify: '',
  }
}

/** Port of IMSService/SaveDealData — the legacy Add Deal save. */
export async function submitActionDeal(payload: ActionDealPayload): Promise<string> {
  if (USE_MOCK) {
    await delay(300)
    console.info('[mock] submitActionDeal', payload)
    return 'Deal Saved'
  }
  const { userId } = getSessionContext()
  const result = await apiPost<string>(ROUTES.saveDeal, {
    LeadId: toNumber(payload.entityId),
    PipelineId: Number(payload.pipelineId) || 0,
    PipelineStage: Number(payload.stageId) || 0,
    Amount: payload.amount,
    ClosureDate: payload.closingDate,
    DealOwner: Number(payload.dealOwner) || userId,
    Probability: payload.probability,
    UserId: userId,
  })
  invalidateLeadDetail()
  return typeof result === 'string' && result ? result : 'Deal Saved'
}

